import type { KpiSourceKind, KpiTarget } from '@zerohuman/shared'
import { request } from '@/api'

/**
 * A task's KPIs as the portal reads them (#900): the definition (target, direction, AMBER band, cadence, source and
 * the team or role KPI it serves), whether a person has signed it off, and the readings taken so far. The light is
 * not sent: the portal derives it with the same `@zerohuman/shared` functions every other layer uses, and only for a
 * KPI that is signed off.
 */
export type TaskKpi = {
    id: string
    /** The task it belongs to. */
    taskId: string
    name: string
    targetValue: number | null
    targetDirection: 'at_least' | 'at_most' | null
    amberBand: number | null
    onTargetWorseningRule: boolean
    cadenceMinutes: number | null
    /** The team or role KPI this one serves, or null when it stands alone. */
    servesKpiId: string | null
    servesName: string | null
    /** Where readings come from: a task that records them, or a person by hand. Null until it is set. */
    sourceKind: KpiSourceKind | null
    sourceRef: string | null
    /** The source's name, or null when there is none. */
    sourceName: string | null
    /** Whether a person has signed it off. Until then it draws no light and takes no reading. */
    signedOff: boolean
    signedOffAt: string | null
    readings: Array<{ value: number; takenAt: string; recordedBy: string }>
}

/**
 * A KPI of any layer that lists its KPIs this way: a task's, or a role's (#901), which has no task. The rows a page
 * draws them with (TaskKpiRows.vue) read only this.
 */
export type LayerKpi = Omit<TaskKpi, 'taskId'> & { taskId?: string }

/** What a person sets as they sign a KPI off: everything a KPI needs to be read against. */
export type TaskKpiSignOff = {
    targetValue: number
    targetDirection: 'at_least' | 'at_most'
    amberBand: number | null
    cadenceMinutes: number
    sourceKind: KpiSourceKind
    sourceRef: string
}

/** A definition change: the task version it published (null when none) and the task's KPIs after it. */
export type TaskKpiChange = { version: number | null; kpis: TaskKpi[] }

/** The task KPIs of the tasks a team owns, for HQ. */
export type TeamTaskKpis = { teamId: string; kpis: TaskKpi[] }

/** `kpi`'s target as the shared derivation reads it, or null for a KPI with no target, which shows no light. */
export function taskKpiTarget(
    kpi: Pick<TaskKpi, 'targetValue' | 'targetDirection' | 'amberBand' | 'onTargetWorseningRule'>,
): KpiTarget | null {
    if (kpi.targetValue === null || !kpi.targetDirection) {
        return null
    }
    return {
        value: kpi.targetValue,
        direction: kpi.targetDirection,
        amberBand: kpi.amberBand ?? 0,
        worsensOnTarget: kpi.onTargetWorseningRule === true,
    }
}

export const taskKpisApi = {
    /** A task's KPIs; those of `versionId` when given, else of the task's latest version. */
    forTask: (taskId: string, versionId?: string): Promise<TaskKpi[]> =>
        request<TaskKpi[]>(
            `/v1/tasks/${encodeURIComponent(taskId)}/kpis${versionId ? `?versionId=${encodeURIComponent(versionId)}` : ''}`,
        ),
    /** Every team's task KPIs, grouped by the owning team. */
    byTeam: (): Promise<TeamTaskKpis[]> => request<TeamTaskKpis[]>('/v1/kpis/tasks-by-team'),
    /**
     * A person signs a KPI off with what it is read against, publishing a task version when that changes it.
     * `baseVersionId` is the task version the page read: a change is refused when the task has moved since (#1427).
     */
    signOff: (taskId: string, kpiId: string, fields: TaskKpiSignOff, baseVersionId?: string): Promise<TaskKpiChange> =>
        request<TaskKpiChange>(`/v1/tasks/${encodeURIComponent(taskId)}/kpis/${encodeURIComponent(kpiId)}/sign-off`, {
            method: 'POST',
            body: JSON.stringify(baseVersionId ? { ...fields, baseVersionId } : fields),
        }),
    /** Withdraws a KPI from the task, publishing a new version without it, against `baseVersionId`, the version read. */
    withdraw: (taskId: string, kpiId: string, baseVersionId?: string): Promise<TaskKpiChange> =>
        request<TaskKpiChange>(
            `/v1/tasks/${encodeURIComponent(taskId)}/kpis/${encodeURIComponent(kpiId)}${baseVersionId ? `?baseVersionId=${encodeURIComponent(baseVersionId)}` : ''}`,
            { method: 'DELETE' },
        ),
}
